import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import BackEndService from "../../api/BackEndService";
import MonitorServiceAction from "../../api/MonitorService";
import { AppointmentDetails } from "../../components/AppointmentDetails/AppointmentDetails";
import { BackButtonHeader } from "../../components/BackButtonHeader/BackButtonHeader";
import { CustomButton } from "../../components/CustomButton/CustomButton";
import { CustomTextArea } from "../../components/CustomTextArea/CustomTextArea";
import { FloatingButtons } from "../../components/FloatingButtons/FloatingButtons";
import { MultiImageUploader2 } from "../../components/MultiImageUploader2/MultiImageUploader2";
import { ReasonListItem } from "../../components/ReasonListItem/ReasonListItem";
import { ServiceInfoBox } from "../../components/ServiceInfoBox/ServiceInfoBox";
import { useHandleServiceImages } from "../../hooks/useHandleServiceImages";
import { AppLayout } from "../../layout/AppLayout/AppLayout";
import { goOneStepBack } from "../../services/NavigationService";
import { RootState } from "../../store";
import { imageUploaderActions } from "../../store/image-uploader-slice";
import { creatingServiceActions } from "../../store/job-slice";
import { MonitorEvent } from "../../store/serviced-monitors";
import { activeTabsActions } from "../../store/tabs-slice";
import styles from "../../styles/unreachable.module.css";
import { ResponseMeta, ResponseWarningQueries } from "../../types/response";

export const UnreachableService = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [monitorEventId, setMonitorEventId] = useState("");
  const [requestLoading, setRequestLoading] = useState(false);
  const [isSelected, setIsSelected] = useState(false);
  const [error, setError] = useState("");

  const { handleImageUploaded, handleImageDelete } = useHandleServiceImages();
  const appointment = useSelector((state: RootState) => state.appointment);
  const service = useSelector((state: RootState) => state.service);
  const monitor = useSelector((state: RootState) => state.monitor.monitor);
  const servicedMonitors = useSelector(
    (state: RootState) => state.servicedMonitors.servicedMonitors
  );
  const monitorEvents = useSelector(
    (state: RootState) => state.servicedMonitors.monitorEvents
  );

  const reasons = [
    { id: "1", name: "Raum verschlossen" },
    { id: "2", name: "Monitor zugestellt" },
  ];
  const handleTextareaChange = (note: string) => {
    dispatch(creatingServiceActions.addComment(note));
  };

  const createMonitorEvent = useCallback(async () => {
    try {
      setRequestLoading(true);
      let response: { data: MonitorEvent } & ResponseMeta &
        ResponseWarningQueries;
      const payload = {
        monitorId: monitor!.id,
        eventType: service.eventType,
        jobId: appointment?.jobs?.slice(-1).pop().id,
        comment: service.comment,
        reason: service.reason,
        sevdesk_products: [],
        infestation: monitor.trapType ?? null,
      };

      if (servicedMonitors?.includes(monitor!.id)) {
        response = await MonitorServiceAction.UpdateMonitorEvent(
          monitorEventId,
          payload
        );
      } else {
        response = await MonitorServiceAction.CreateMonitorEvent(payload);
      }

      if (response.data) {
        if (service.images.length > 0) {
          const imageUploadPromises = service.images.map((image) => {
            return BackEndService.AddDocument({
              documentType: "userUpload",
              fileName: image.name,
              isInternal: true,
              media: image.uuid,
              attachedTo: {
                type: "MonitorEvent",
                id: response.data.id,
              },
            });
          });

          // Wait for all image uploads to complete before proceeding
          await Promise.all(imageUploadPromises).then(() => response);
          setRequestLoading(false);
        }
        returnToDashboard();
      }
    } catch (err: any) {
      setRequestLoading(false);
      const errorMessage =
        err?.error &&
        typeof err.error === "string" &&
        err.error.toLowerCase().includes("description")
          ? t("IncorrectMaterial")
          : t("MonitorIsRemoved");

      setError(errorMessage);
    }
  }, [
    dispatch,
    service,
    monitor,
    service.images.length,
    appointment,
    navigate,
  ]);

  const isReasonChosen = () => {
    return !!service.reason?.trim();
  };

  const [, setIsCreateMonitorEventButtonDisabled] = useState(!isReasonChosen());

  const returnToDashboard = () => {
    dispatch(activeTabsActions.setDashbaordUrl(""));
    dispatch(activeTabsActions.setMonitorsStep(2));
    dispatch(creatingServiceActions.resetState());
    dispatch(imageUploaderActions.resetState());

    navigate(`/properties/${appointment.id}/show`);
  };

  useEffect(() => {
    setIsCreateMonitorEventButtonDisabled(!isReasonChosen());
  }, [service.reason]);

  useEffect(() => {
    setMonitorEventId(
      monitorEvents.find(
        (event: MonitorEvent) => event.monitorId === monitor.id
      )?.id ?? ""
    );
  }, [monitorEvents]);

  const handleBackButtonClicked = () => {
    goOneStepBack(navigate);

    dispatch(creatingServiceActions.addComment(""));
  };
  const handleClickClock = () => {
    dispatch(creatingServiceActions.resetState());
  };

  const handleSelect = (isSelected: boolean) => {
    setIsSelected(isSelected);
  };

  const handleLoadingChange = (loading: boolean) => {
    setRequestLoading(loading);
  };

  return (
    <AppLayout hideHeader>
      <BackButtonHeader
        handleBackButtonClicked={handleBackButtonClicked}
        showDocumentation={false}
      />

      <AppointmentDetails handleClockClickReset={handleClickClock} />

      <div className={styles["content-wrapper"]}>
        <ServiceInfoBox
          data={monitor}
          typeOfService="Nicht erreichbar"
          color="white"
          trapType={monitor.trapType} 
          hideCheck
        />

        <p className={styles["title"]}>{t("ChooseReason")}</p>

        <div className={styles["reason-list"]}>
          {reasons.map((reason, i) => (
            <ReasonListItem
              key={i}
              reason={reason.name}
              onSelect={handleSelect}
            />
          ))}
        </div>

        <MultiImageUploader2
          images={service.images}
          onImageUpload={handleImageUploaded}
          onDeleteImage={handleImageDelete}
          onLoadingChange={handleLoadingChange}
        />

        <CustomTextArea
          placeholder="Zusätzlicher Text"
          onTextareaChange={handleTextareaChange}
          defaultValue={service.comment}
        />

        {error && <div className={styles["error-message"]}>{error}</div>}

        <FloatingButtons>
          <CustomButton
            color="green"
            onClick={createMonitorEvent}
            width={60}
            loading={requestLoading}
            disabled={!isSelected || requestLoading}
          >
            {t("Confirm")}
          </CustomButton>
        </FloatingButtons>
      </div>
    </AppLayout>
  );
};
